import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiCall } from "../main/Apicall";
import "../css/others/Card.css";
import ToastPopup from "../Popups/ToastPopup";
import { createToastMessage, ExtendedClientInfo, ToastMessage } from "../Interfaces/Interface";
import { ValidationRule, validateField, nameValidationRules, emailValidationRules } from "../Reusablecomponents/Validation";
const CreateClient: React.FC = () => {
  const [client, setClient] = useState<ExtendedClientInfo & { createdBy?: string; createdDate?: string }>({ clientId: "", clientName: "", subdomain: "", brandingConfig: "", primarySpocName: "", primarySpocEmail: "", secondarySpocName: "", secondarySpocEmail: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [isUploadMode, setIsUploadMode] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const validationRules: Record<string, ValidationRule[]> = {
    clientName: [...nameValidationRules, { maxLength: 60, customValidator: (value) => value.length > 60 ? "Client name must be less than 60 characters" : null }],
    primarySpocName: [...nameValidationRules, { maxLength: 60, customValidator: (value) => value.length > 60 ? "Primary SPOC name must be less than 60 characters" : null }],
    secondarySpocName: [...nameValidationRules, { maxLength: 60, customValidator: (value) => value.length > 60 ? "Secondary SPOC name must be less than 60 characters" : null }],
    primarySpocEmail: emailValidationRules,
    secondarySpocEmail: emailValidationRules,
    subdomain: [{ required: true, minLength: 2, maxLength: 50 }, { customValidator: (value) => /\s/.test(value) ? "Subdomain must not contain spaces" : null }]
  };
  useEffect(() => {
    const uploadedData = sessionStorage.getItem("uploadedClientData");
    if (uploadedData) {
      try {
        const jsonData = JSON.parse(uploadedData);
        setClient((prev) => ({ ...prev, ...jsonData }));
        setMessage(createToastMessage("Client data loaded from uploaded JSON!", "warning", 3000));
        sessionStorage.removeItem("uploadedClientData");
      } catch (err) {
        console.error("Error parsing uploaded data", err);
      }
    }
    const params = new URLSearchParams(location.search);
    if (params.get("upload") === "true") {
      setIsUploadMode(true);
    }
  }, [location]);
  const handleInputChange = (key: keyof typeof client, event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (key === "subdomain") {
      value = value.replace(/\s+/g, "").toLocaleLowerCase();
    }
    setClient({ ...client, [key]: value });
    if (validationRules[key]) {
      const error = validateField(
        value,
        validationRules[key],
        String(key).replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
      );
      setErrors((prev) => ({ ...prev, [key]: error || "" }));
    }
  };
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      if (jsonData.subdomain) {
        jsonData.subdomain = jsonData.subdomain.replace(/\s+/g, "");
      }
      setClient({ ...client, ...jsonData });
      setMessage(createToastMessage("Client data loaded successfully from JSON!", "success", 3000));
      validateAllFields({ ...client, ...jsonData });
    } catch (error) {
      setMessage(createToastMessage("Invalid JSON file.", "error"));
    }
  };
  const validateAllFields = (data: typeof client) => {
    const newErrors: Record<string, string> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (validationRules[key]) {
        const error = validateField(
          String(value || ""),
          validationRules[key],
          key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
        );
        if (error) {
          newErrors[key] = error;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    const requiredFields = ["clientName", "subdomain", "primarySpocName", "primarySpocEmail", "secondarySpocName", "secondarySpocEmail"];
    const emptyRequiredFields = requiredFields.filter(
      (field) => !client[field as keyof typeof client]?.trim()
    );
    if (emptyRequiredFields.length > 0) {
      setMessage(createToastMessage("Please fill all required fields.", "warning", 3000));
      const newErrors: Record<string, string> = {};
      emptyRequiredFields.forEach((field) => {
        newErrors[field] = "This field is required";
      });
      setErrors(newErrors);
      return;
    }
    const isValid = validateAllFields(client);
    if (!isValid) {
      if (Object.keys(errors).length > 1) {
        setMessage(createToastMessage("Please fix the validation errors before submitting.", "warning", 1500));
      } else {
        const firstErrorKey = Object.keys(errors)[0];
        setMessage(createToastMessage((errors[firstErrorKey]), "warning", 1500));
      }
      return;
    }
    setShowConfirm(true);
  };
  const confirmSubmit = async () => {
    setShowConfirm(false);
    try {
      const payload = { clientName: client.clientName, subdomain: client.subdomain, brandingConfig: client.brandingConfig, primarySpocName: client.primarySpocName, primarySpocEmail: client.primarySpocEmail, secondarySpocName: client.secondarySpocName, secondarySpocEmail: client.secondarySpocEmail, createdBy: "admin" };
      const response = await apiCall("post", "/api/clients", payload);
      const createdClientId = response?.clientId;
      if (!createdClientId) {
        setMessage(createToastMessage("Client created but ID not returned.", "error", 3000));
        return;
      }
      setMessage(createToastMessage("Client created successfully!", "success", 3000));
      setTimeout(async () => {
        try {
          await apiCall("post", "/api/global-mappings", undefined, { clientId: createdClientId });
          setMessage(createToastMessage("Global mappings fetched successfully!", "success", 2000));
          navigate("/idpConfiguration");
        } catch (error) {
          setMessage(createToastMessage("Error fetching global mappings.", "error", 2000));
        }
      }, 3000);
    } catch (error) {
      setMessage(createToastMessage("Error in client creation. Please try again.", "error", 3000));
    }
  };
  const handleBackClick = () => { navigate(-1); };
  const iconMapper: { [key: string]: string } = { clientName: "fas fa-user", subdomain: "fas fa-globe", primarySpocName: "fas fa-user-shield", primarySpocEmail: "fas fa-envelope", secondarySpocName: "fas fa-user-shield", secondarySpocEmail: "fas fa-envelope", brandingConfig: "fas fa-paint-brush", createdBy: "fas fa-user-edit", createdDate: "fas fa-calendar-alt" };
  return (
    <div className="container">
      <h1 className="titlee">Create Client</h1>
      {isUploadMode && (
        <div className="upload-section">
          <input type="file" accept="application/json" onChange={handleFileUpload} />
        </div>
      )}
      <div className="card-container">
        <div className="grid">
          {Object.entries(client).map(([key, value]) => {
            if (key === "clientId") return null;
            const iconClass = iconMapper[key] || "fas fa-question";
            const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
            const fieldError = errors[key];
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="icon">  <i className={iconClass}></i></div>
                  <div className="content">
                    <div className="label">{label}</div>
                    {key === "createdDate" ? (
                      <input type="text" className={`input ${fieldError ? "error" : ""}`} value={String(value || "")} readOnly />
                    ) : (
                      <input type="text" className={`input ${fieldError ? "error" : ""}`} value={String(value || "")} onChange={(event) => handleInputChange(key as keyof typeof client, event)} onBlur={(event) => handleInputChange(key as keyof typeof client, event)} />)}
                    {fieldError && (
                      <div className="error-message">{fieldError}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <nav className="buttonss">
        <button className="back-button" onClick={handleBackClick}> Back </button>
        <button className="submit-button" onClick={handleSubmit}> Submit </button>
      </nav>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Please confirm</h3>
            <p> Please verify the given data. Once the subdomain is set, it cannot be changed. </p>
            <div className="buttons">
              <button className="savebutton" onClick={confirmSubmit}> Confirm & Save</button>
              <button className="cancelbutton" onClick={() => setShowConfirm(false)}> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CreateClient;